import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { getCurrentSession, signIn, signUp, signOut, completeNewPassword as cognitoCompleteNewPassword, NewPasswordRequiredError } from '../services/cognito'
import { updateApiToken } from '../services/api'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { setUser, clearUser, fetchCredits } from '../store/userSlice'
import { creditsAPI } from '../services/api'
import { ga4, UserTier } from '../services/ga4'
import { logger } from '../utils/logger'

interface PendingPasswordChange {
  cognitoUser: CognitoUser
  userAttributes: Record<string, string>
  email: string
}

interface AuthContextType {
  token: string | null
  user: any | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, referralCode?: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
  isAuthenticated: boolean
  // New password change flow
  pendingPasswordChange: PendingPasswordChange | null
  completeNewPassword: (newPassword: string) => Promise<void>
  cancelPasswordChange: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [pendingPasswordChange, setPendingPasswordChange] = useState<PendingPasswordChange | null>(null)
  const user = useAppSelector((state) => state.user.user)

  const checkSession = useCallback(async () => {
    try {
      const session = await getCurrentSession()
      if (session) {
        setToken(session.accessToken)
        updateApiToken(session.accessToken)

        // Store user data in Redux instead of local state
        dispatch(setUser({
          userId: session.userId,
          email: session.email,
          tier: session.tier,
          credits: session.credits,
          stripeCustomerId: session.stripeCustomerId
        }))

        // Fetch real-time credits from DynamoDB (will update user.credits via fetchCredits.fulfilled)
        dispatch(fetchCredits())

        // GA4: Set user for returning sessions
        ga4.setUser(session.userId, { tier: session.tier as UserTier })
      }
    } catch (error) {
      logger.error('Session check failed', error, { component: 'AuthContext' })
    } finally {
      setLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    // Check for existing Cognito session on mount
    checkSession()
  }, [checkSession])

  const login = useCallback(async (email: string, password: string) => {
    try {
      const authUser = await signIn(email, password)

      // Set token for auth
      setToken(authUser.accessToken)
      updateApiToken(authUser.accessToken)

      // Store user data in Redux
      dispatch(setUser({
        userId: authUser.userId,
        email: authUser.email,
        tier: authUser.tier,
        credits: authUser.credits,
        stripeCustomerId: authUser.stripeCustomerId
      }))

      // Apply pending referral code if exists (from signup via referral link)
      const pendingReferralCode = localStorage.getItem('pendingReferralCode')
      if (pendingReferralCode) {
        try {
          await creditsAPI.initialize(pendingReferralCode)
          localStorage.removeItem('pendingReferralCode')
        } catch {
          // Initialization may already exist — safe to ignore
          localStorage.removeItem('pendingReferralCode')
        }
      }

      // Fetch real-time credits from DynamoDB (will update user.credits via fetchCredits.fulfilled)
      dispatch(fetchCredits())

      // GA4: Track successful login and set user
      ga4.authLoginSuccess('email', authUser.tier as UserTier)
      ga4.setUser(authUser.userId, { tier: authUser.tier as UserTier })
    } catch (error: any) {
      // Check if this is a NEW_PASSWORD_REQUIRED challenge
      if (error instanceof NewPasswordRequiredError) {
        // Store the pending password change state
        setPendingPasswordChange({
          cognitoUser: error.cognitoUser,
          userAttributes: error.userAttributes,
          email
        })
        // Throw a specific error that the UI can handle
        throw new Error('NEW_PASSWORD_REQUIRED')
      }
      // GA4: Track login failure
      ga4.authLoginFailure('email', error.code || 'unknown')
      throw new Error(error.message || 'Login failed')
    }
  }, [dispatch])

  const register = useCallback(async (email: string, password: string, referralCode?: string) => {
    // GA4: Track signup start
    ga4.authSignupStart()

    try {
      await signUp(email, password, referralCode)

      // GA4: Track signup complete (pending verification)
      ga4.authSignupComplete('STARTER')

      // User needs to verify email - throw a specific error to redirect to verify page
      throw new Error('VERIFICATION_REQUIRED')
    } catch (error: any) {
      if (error.message === 'VERIFICATION_REQUIRED') {
        throw error
      }
      // GA4: Track signup failure
      ga4.authSignupFailure(error.code || 'unknown')
      throw new Error(error.message || 'Registration failed')
    }
  }, [])

  const logout = useCallback(() => {
    // GA4: Track logout and clear user
    ga4.authLogout()
    ga4.clearUser()

    // Sign out from Cognito
    signOut()

    // Clear local state
    setToken(null)
    updateApiToken(null)

    // Clear user data from Redux
    dispatch(clearUser())

    // Clear all storage (localStorage and sessionStorage)
    localStorage.clear()
    sessionStorage.clear()
  }, [dispatch])

  const refreshUser = useCallback(async () => {
    // Refresh credits from DynamoDB
    await dispatch(fetchCredits())
    // Optionally re-check session
    await checkSession()
  }, [dispatch, checkSession])

  const completeNewPassword = useCallback(async (newPassword: string) => {
    if (!pendingPasswordChange) {
      throw new Error('No pending password change')
    }

    try {
      const authUser = await cognitoCompleteNewPassword(
        pendingPasswordChange.cognitoUser,
        newPassword,
        pendingPasswordChange.userAttributes
      )

      // Clear the pending state
      setPendingPasswordChange(null)

      // Set token for auth
      setToken(authUser.accessToken)
      updateApiToken(authUser.accessToken)

      // Store user data in Redux
      dispatch(setUser({
        userId: authUser.userId,
        email: authUser.email,
        tier: authUser.tier,
        credits: authUser.credits,
        stripeCustomerId: authUser.stripeCustomerId
      }))

      // Fetch real-time credits from DynamoDB
      dispatch(fetchCredits())

      // GA4: Track successful login and set user
      ga4.authLoginSuccess('email', authUser.tier as UserTier)
      ga4.setUser(authUser.userId, { tier: authUser.tier as UserTier })
    } catch (error: any) {
      ga4.authLoginFailure('email', error.code || 'password_change_failed')
      throw new Error(error.message || 'Failed to set new password')
    }
  }, [dispatch, pendingPasswordChange])

  const cancelPasswordChange = useCallback(() => {
    setPendingPasswordChange(null)
  }, [])

  const contextValue = useMemo(() => ({
    token,
    user,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!token,
    pendingPasswordChange,
    completeNewPassword,
    cancelPasswordChange
  }), [token, user, login, register, logout, refreshUser, pendingPasswordChange, completeNewPassword, cancelPasswordChange])

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
